/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useLazyCreatePaymentIntentQuery } from "../../../../store/services/PaymentService";
import {
  useLazyConfirmPaymentQuery,
  useLazyConfirmReservationQuery,
} from "../../../../store/services/ReservationService";
import { ReservationListInterface } from "../../../../store/slices/reservationSlice";
import { showToast } from "../../../../store/slices/toastSlice";
import { useTypedDispatch, useTypedSelector } from "../../../../store/store";
import TOAST from "../../../../utils/constants/toast";
import { prepareParams } from "../../../../utils/helpers/rooms";
import LoadingContainer from "../../../containers/LoadingContainer";
import Button from "../../../shared/Button";
import LegalData from "../../molecules/LegalData";
import PriceType from "../../molecules/PriceType";

import { usePaymentState } from "./hooks/usePaymentState";
import styles from "./index.module.scss";

interface TransactionType {
  full?: number;
  deposit?: number;
  amount: number;
  currency: string;
  transaction_type: string;
}

export interface StripeProviderProps {
  mode?: "page" | "modal";
  amount?: number;
  currency?: string;
  pnr?: string;
  source?: string;
  onSuccess?: () => void;
  onFail?: (message?: string) => void;
  hideTitleAndPriceType?: boolean;
  reservation?: ReservationListInterface;
}

function PaymentContinueForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cruiseId } = useParams();
  const { rooms } = useTypedSelector((state) => state.rooms);

  const params = useMemo(() => {
    const totalRooms = +(searchParams.get("rooms") ?? 0);
    const currentRoomNumber = +(searchParams.get("room") ?? 0);

    return prepareParams(
      searchParams,
      rooms ?? {},
      currentRoomNumber,
      currentRoomNumber < totalRooms,
    );
  }, [searchParams, rooms]);

  const onContinue = () => {
    navigate(`/search-results/${cruiseId}/payment-confirmation?${params}`);
  };

  return (
    <div className={styles.inputs}>
      <Button label="Continue" onClick={onContinue} />
    </div>
  );
}

function StripePaymentForm({
  transaction,
  pnrOverride,
  sourceOverride,
  mode,
  onSuccess,
  onFail,
  reservation,
}: {
  transaction: TransactionType;
  pnrOverride?: string;
  sourceOverride?: string;
  mode: "page" | "modal";
  onSuccess?: () => void;
  onFail?: (message?: string) => void;
  reservation?: ReservationListInterface;
}) {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const { t } = useTranslation();
  const [createIntent] = useLazyCreatePaymentIntentQuery();
  const [confirmReservation] = useLazyConfirmReservationQuery();
  const [confirmPayment] = useLazyConfirmPaymentQuery();
  const [searchParams] = useSearchParams();

  const { cruiseId } = useParams();
  const { rooms } = useTypedSelector((state) => state.rooms);

  const sourceFromUrl = searchParams.get("source") ?? "";
  const source = sourceOverride ?? sourceFromUrl;

  const {
    payment_checkbox_1,
    payment_checkbox_2,
    payment_checkbox_3,
    payment_checkbox_4,
    payment_checkbox_5,
    payment_checkbox_6,
  } = useTypedSelector((state) => state.environment);

  const stripe = useStripe();
  const elements = useElements();

  const { isLoading, setIsLoading, errorMessage, setErrorMessage } =
    usePaymentState();

  const [legalData, setLegalData] = useState({
    checkbox_1: !payment_checkbox_1,
    checkbox_2: !payment_checkbox_2,
    checkbox_3: !payment_checkbox_3,
    checkbox_4: !payment_checkbox_4,
    checkbox_5: !payment_checkbox_5,
    checkbox_6: !payment_checkbox_6,
  });

  const params = useMemo(() => {
    if (mode !== "page") {
      return "";
    }

    const totalRooms = +(searchParams.get("rooms") ?? 0);
    const currentRoomNumber = +(searchParams.get("room") ?? 0);

    return prepareParams(
      searchParams,
      rooms ?? {},
      currentRoomNumber,
      currentRoomNumber < totalRooms,
    );
  }, [searchParams, rooms, mode]);

  const handleLegalDataChange = ({
    value,
    valueKey,
  }: {
    value: boolean;
    valueKey: string;
  }) => {
    setLegalData((prev) => ({ ...prev, [valueKey]: value }));
  };

  const defaultFail = (msg?: string) => {
    dispatch(
      showToast({
        type: TOAST.ERROR_TYPE,
        message: msg || t("Payment failed"),
        duration: TOAST.DEFAULT_DURATION,
      }),
    );
  };

  const defaultSuccess = () => {
    dispatch(
      showToast({
        type: TOAST.SUCCESS_TYPE,
        message: t("Payment success"),
        duration: TOAST.DEFAULT_DURATION,
      }),
    );
  };

  const onPaymentFail = (msg?: string) => {
    if (onFail) {
      onFail(msg);
    } else {
      defaultFail(msg);
    }
  };

  const onPaymentSuccess = () => {
    if (onSuccess) {
      onSuccess();

      return;
    }

    defaultSuccess();
    navigate(`/search-results/${cruiseId}/payment-confirmation?${params}`);
  };

  const handleConfirm = async () => {
    if (!stripe || !elements) {
      onPaymentFail(t("Stripe not initialized"));

      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const { error: submitError } = await elements.submit();

      if (submitError) {
        throw new Error(submitError.message);
      }

      const { amount, currency } = transaction;

      const pnrToUse = pnrOverride ?? rooms?.[1]?.pnr ?? "";

      if (!pnrToUse) {
        throw new Error("PNR missing");
      }

      const { data } = await createIntent({
        pnr: pnrToUse,
        transactions: [{ amount, currency }],
        source,
      });

      if (!data?.client_secret) {
        throw new Error("Failed to create payment intent");
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: data.client_secret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (error) {
        throw new Error(error.message || "Stripe payment failed");
      }

      const intentTransactions = [
        {
          transaction_type: "creditcard" as const,
          made_on: dayjs(paymentIntent.created * 1000).format(
            "YYYY-MM-DD hh:mm:ss",
          ),
          made_by: "Website" as const,
          amount: (paymentIntent.amount / 100).toFixed(2),
          currency: paymentIntent.currency,
          card_clearance: {
            clearance_system: "stripe" as const,
            auth_code: paymentIntent.id,
            transaction_id: paymentIntent.id,
            other: {
              card_type:
                (paymentIntent.payment_method as any)?.card?.brand || "unknown",
              last_four:
                (paymentIntent.payment_method as any)?.card?.last4 || "0000",
              transaction_id: paymentIntent.id,
              intent_id: paymentIntent.id,
            },
          },
        },
      ];

      if (reservation?.status === "confirmed") {
        await confirmPayment({
          pnr: pnrToUse,
          transactions: intentTransactions,
        });
      } else {
        await confirmReservation({
          pnr: pnrToUse,
          transactions: intentTransactions,
        });
      }

      setIsLoading(false);
      onPaymentSuccess();
    } catch (error) {
      const errorText =
        error instanceof Error ? error.message : JSON.stringify(error);
      setIsLoading(false);
      onPaymentFail(errorText);
      setErrorMessage(errorText);
    }
  };

  useEffect(() => {
    if (mode === "page") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [mode]);

  const legalDataAccepted =
    legalData.checkbox_1 &&
    legalData.checkbox_2 &&
    legalData.checkbox_3 &&
    legalData.checkbox_4 &&
    legalData.checkbox_5 &&
    legalData.checkbox_6;

  return (
    <>
      {errorMessage.length > 0 && (
        <div className={styles.inputError}>{errorMessage}</div>
      )}

      <div className={styles.fieldContainer}>
        <PaymentElement />
      </div>

      <LegalData
        checkbox_1_checked={legalData.checkbox_1}
        checkbox_2_checked={legalData.checkbox_2}
        checkbox_3_checked={legalData.checkbox_3}
        checkbox_4_checked={legalData.checkbox_4}
        checkbox_5_checked={legalData.checkbox_5}
        checkbox_6_checked={legalData.checkbox_6}
        onChange={handleLegalDataChange}
      />

      <div className={styles.inputs}>
        <Button
          label={isLoading ? t("Loading") : t("Pay")}
          className={styles.submitButton}
          onClick={handleConfirm}
          loading={isLoading}
          disabled={isLoading || !legalDataAccepted || !stripe || !elements}
        />
      </div>
    </>
  );
}

function StripeProvider({
  mode = "page",
  amount,
  currency,
  pnr,
  source,
  onSuccess,
  onFail,
  hideTitleAndPriceType,
  reservation,
}: StripeProviderProps) {
  const { rooms } = useTypedSelector((state) => state.rooms);
  const { stripe_publishable_key, payment_option_text } = useTypedSelector(
    (state) => state.environment,
  );
  const { isCruiseLoading } = useTypedSelector((state) => state.search);
  const { t } = useTranslation();
  const { priceType, setPriceType } = usePaymentState();

  const transactions: TransactionType[] = useMemo(() => {
    if (mode === "modal") {
      return [
        {
          amount: amount ?? 0,
          currency: currency ?? "USD",
          transaction_type: "creditcard",
          full: undefined,
          deposit: undefined,
        },
      ];
    }

    const output: TransactionType = {
      full: 0,
      deposit: 0,
      amount: 0,
      currency: rooms?.[1]?.pricing?.payment_schedule?.[0].currency ?? "",
      transaction_type: "creditcard",
    };

    const deposit = Object.values(rooms ?? {}).reduce((sum, room) => {
      const roomDeposit = room?.pricing?.payment_schedule?.[0]?.amount ?? 0;

      return Number(sum) + Number(roomDeposit);
    }, 0);

    output.deposit = deposit;

    Object.values(rooms ?? {}).forEach((room) => {
      const paymentSchedule = room?.pricing?.payment_schedule;
      if (Array.isArray(paymentSchedule)) {
        paymentSchedule.forEach((payment) => {
          if (payment.amount) {
            output.full = (output.full ?? 0) + parseFloat(payment.amount);
          }
        });
      }
    });

    if (priceType === "full") {
      output.amount = output.full ?? 0;
    }

    if (priceType === "deposit") {
      output.amount = output.deposit ?? 0;
    }

    output.amount = Math.ceil((output.amount ?? 0) * 100) / 100;
    output.full = Math.ceil((output.full ?? 0) * 100) / 100;
    output.deposit = Math.ceil((output.deposit ?? 0) * 100) / 100;

    return [output];
  }, [rooms, priceType, mode, amount, currency]);

  const handlePriceTypeChange = (type: typeof priceType) => setPriceType(type);

  const selectedTransaction = transactions[0];
  const stripePromise = useMemo(
    () => loadStripe(stripe_publishable_key),
    [stripe_publishable_key],
  );

  const elementsKey = `${mode}-${priceType}`;

  const isOption =
    mode === "page" &&
    Boolean(payment_option_text) &&
    priceType === payment_option_text;

  const shouldUseStripe =
    mode === "modal" || (!isOption && (selectedTransaction?.amount ?? 0) > 0);

  const elementsCurrency = (
    selectedTransaction?.currency || "USD"
  ).toLowerCase();

  return (
    <div className={styles.container}>
      {!hideTitleAndPriceType && (
        <>
          <h1 className={styles.title}>{t("Payment")}</h1>
          <LoadingContainer isLoading={isCruiseLoading}>
            <PriceType
              full={selectedTransaction?.full ?? 0}
              deposit={selectedTransaction?.deposit ?? 0}
              currency={selectedTransaction?.currency || "USD"}
              onPriceTypeChange={handlePriceTypeChange}
              priceType={priceType}
            />
          </LoadingContainer>
        </>
      )}

      <form className={styles.form}>
        {shouldUseStripe ? (
          <Elements
            key={elementsKey}
            stripe={stripePromise}
            options={{
              mode: "payment",
              currency: elementsCurrency,
              amount: Math.round((selectedTransaction.amount || 1) * 100),
            }}>
            <StripePaymentForm
              mode={mode}
              transaction={selectedTransaction}
              pnrOverride={pnr}
              sourceOverride={source}
              onSuccess={onSuccess}
              onFail={onFail}
              reservation={reservation}
            />
          </Elements>
        ) : (
          <PaymentContinueForm />
        )}
      </form>
    </div>
  );
}

export default StripeProvider;
